import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { PawPrint, ChartLineUp, TwitterLogo, FacebookLogo, LinkSimple, EnvelopeSimple, Check } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { fbTrack } from "@/lib/fbpixel";

const CREDITS = [
  { h: "Mr. Wigglez presents" },
  { big: "HEAVEN" },
  { s: "A true story of struggle, loss & loyalty." },
  { s: "For Smiley · Beats · King — and every pit still waiting." },
  { div: true },
  { h: "The Mission" },
  { s: "300 acres · 3,000 pits rescued · a $3,000,000 sanctuary." },
  { s: "Every single piece of clothing donates to the cause." },
  { s: "A LIVE tracker counts every dollar — watch it climb." },
  { div: true },
  { h: "Your One-Click Tour" },
  { s: "Shop the Drop · 1-of-1 Pet Collab · Virtual Try-On" },
  { s: "Rescue Guardian Wall · Neon Wrap · Mr Hub" },
  { s: "Walk the Heaven Ranch Map" },
  { div: true },
  { h: "Thank you for joining the fam" },
  { s: "You are the reason the gates open." },
  { s: "Art that keeps them alive." },
  { div: true },
  { s: "Directed by Ryan · Mr. Wigglez" },
];

export default function HeavenCredits() {
  const [email, setEmail] = useState("");
  const [joined, setJoined] = useState(false);
  const [busy, setBusy] = useState(false);
  const shareUrl = typeof window !== "undefined" ? window.location.origin : "https://mrwigglez.com";
  const shareText = "All Dogs Go to Heaven — help build a 300-acre pitbull sanctuary with Mr. Wigglez.";

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(shareUrl); toast.success("Link copied — spread the word 🐾"); }
    catch { toast.error("Couldn't copy the link"); }
  };
  const tweet = () => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`, "_blank");
  const fb = () => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, "_blank");

  const join = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) { toast.error("Enter a valid email"); return; }
    setBusy(true);
    try { await api.post("/subscribe", { email, source: "credits" }); fbTrack("Lead", { content_name: "newsletter-credits" }); setJoined(true); toast.success("Welcome to the pack 🐾"); }
    catch { toast.error("Something went wrong — try again"); }
    finally { setBusy(false); }
  };

  return (
    <section className="relative bg-black py-20 overflow-hidden" data-testid="heaven-credits">
      <div className="absolute inset-0" style={{ background: "radial-gradient(120% 70% at 50% 0%, #10041a 0%, #05040a 60%, #000 100%)" }} />
      <div className="relative max-w-3xl mx-auto px-6">
        {/* rolling credits screen */}
        <div className="relative h-80 sm:h-96 overflow-hidden rounded-xl border border-primary/20 bg-black/60 backdrop-blur neon-glow" data-testid="credits-roll">
          <div className="pointer-events-none absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-black to-transparent z-10" />
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black to-transparent z-10" />
          <motion.div
            initial={{ y: "60%" }}
            animate={{ y: "-115%" }}
            transition={{ duration: 42, ease: "linear", repeat: Infinity }}
            className="absolute inset-x-0 px-6 text-center space-y-5"
          >
            {CREDITS.map((c, n) => (
              <div key={n}>
                {c.big && <p className="font-display text-5xl sm:text-6xl neon-text tracking-tight">{c.big}</p>}
                {c.h && <p className="overline text-secondary">{c.h}</p>}
                {c.s && <p className="text-white/90 font-modern text-base sm:text-lg leading-relaxed">{c.s}</p>}
                {c.div && <div className="mx-auto my-6 h-px w-16 bg-primary/40" />}
              </div>
            ))}
          </motion.div>
        </div>

        {/* live tracker CTA */}
        <div className="mt-8 text-center">
          <Link to="/donate" className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-7 py-3.5 rounded-[10px] font-modern font-medium hover:bg-primary/90 transition-colors" data-testid="credits-tracker-cta">
            <ChartLineUp weight="bold" size={18} /> See the Live $3M Tracker
          </Link>
        </div>

        {/* spread the word */}
        <div className="mt-10 grid sm:grid-cols-2 gap-8 items-start">
          <div>
            <p className="overline text-primary mb-3 inline-flex items-center gap-2"><PawPrint weight="fill" size={14} /> Spread the word</p>
            <div className="flex gap-3">
              <button onClick={copyLink} className="h-11 w-11 grid place-items-center rounded-full border border-white/15 text-white hover:text-primary hover:border-primary/50 transition-colors" data-testid="share-copy" title="Copy link"><LinkSimple size={18} /></button>
              <button onClick={tweet} className="h-11 w-11 grid place-items-center rounded-full border border-white/15 text-white hover:text-primary hover:border-primary/50 transition-colors" data-testid="share-x" title="Share on X"><TwitterLogo weight="fill" size={18} /></button>
              <button onClick={fb} className="h-11 w-11 grid place-items-center rounded-full border border-white/15 text-white hover:text-primary hover:border-primary/50 transition-colors" data-testid="share-fb" title="Share on Facebook"><FacebookLogo weight="fill" size={18} /></button>
            </div>
          </div>
          <div>
            <p className="overline text-secondary mb-3 inline-flex items-center gap-2"><EnvelopeSimple weight="fill" size={14} /> Join the pack</p>
            {joined ? (
              <p className="text-white font-modern inline-flex items-center gap-2" data-testid="newsletter-done"><Check weight="bold" size={18} className="text-primary" /> You're in. Drop alerts & mission updates incoming.</p>
            ) : (
              <form onSubmit={join} className="flex gap-2" data-testid="newsletter-form">
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com" required
                  className="flex-1 bg-white/5 border border-white/15 rounded-[10px] px-4 py-2.5 text-sm text-white outline-none focus:border-primary/50" data-testid="newsletter-email" />
                <button type="submit" disabled={busy} className="bg-secondary text-secondary-foreground px-5 py-2.5 rounded-[10px] font-modern font-medium hover:opacity-90 transition-opacity disabled:opacity-50" data-testid="newsletter-submit">
                  {busy ? "…" : "Sign up"}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
